<template>
	<AppDropdown
		:popover-props="{
			distance: 14,
			placement: 'bottom',
		}"
		is-initially-open
		@hide="$emit('close')"
	>
		<article class="time-off-list-popover">
			<header class="time-off-list-popover__header">
				<h4>{{ $t('time_off') }}</h4>
				<div
					class="time-off-list-popover__row time-off-list-popover__row--header"
				>
					<h5
						v-if="columns.includes(COLUMN_USER)"
						:data-column="COLUMN_USER"
						class="time-off-list-popover__column-header"
					>
						{{ $t('time-off__user') }}
					</h5>
					<h5
						v-if="columns.includes(COLUMN_AVAILABILITY)"
						:data-column="COLUMN_AVAILABILITY"
						class="time-off-list-popover__column-header"
					>
						{{ $t('time-off__availability') }}
					</h5>
					<h5
						v-if="columns.includes(COLUMN_PERIOD)"
						:data-column="COLUMN_PERIOD"
						class="time-off-list-popover__column-header"
					>
						{{ $t('time-off__period') }}
					</h5>
				</div>
			</header>
			<TimeOffListPopoverSkeleton
				v-if="loadingTimeOffList"
				:users-count="userIds.length"
				:columns="columns"
			/>
			<ul v-else class="time-off-list-popover__list">
				<li
					v-for="{ user, timeOffs, availability } in usersTimeOffs"
					:key="user.id"
					class="time-off-list-popover__row time-off-list-popover__row--list"
				>
					<div
						v-if="columns.includes(COLUMN_USER)"
						:data-column="COLUMN_USER"
						class="time-off-list-popover__user-wrapper"
					>
						<div class="time-off-list-popover__user-details">
							<SuiAvatar
								:url="user.profilePicture?.mini"
								:initials="user.initials || ''"
								:color="user?.hslColor || ''"
								size="xx-small"
							/>
							<strong class="time-off-list-popover__highlighted">
								{{ user.name }}
							</strong>
						</div>

						<div
							class="time-off-list-popover__time-off-entries-wrapper"
						>
							<AppDropdown
								:popover-props="{
									distance: 0,
									placement: 'bottom',
								}"
								:trigger-button-props="{
									disabled: timeOffs?.length === 1,
								}"
							>
								<template #trigger>
									<div
										class="time-off-list-popover__time-off-entries"
										:class="{
											'time-off-list-popover__time-off-entries--active':
												timeOffs?.length !== 1,
										}"
									>
										<ul
											v-if="
												timeOffs && timeOffs.length > 0
											"
											class="time-off-list-popover__labels"
										>
											<TimeOffEntryLabel
												v-for="(
													timeOff, index
												) in timeOffs"
												:key="timeOff.id"
												:time-off="timeOff"
												:add-comma-after-label="
													index < timeOffs.length - 1
												"
											/>
										</ul>

										<SuiIcon
											v-if="
												timeOffs && timeOffs.length > 1
											"
											class="time-off-list-popover__icon"
											name="icon-arrow-down-16"
										/>
									</div>
								</template>
								<TimeOffListPopoverDropdown
									:time-offs="timeOffs"
									:user="user"
									@save="onSave"
								/>
							</AppDropdown>
						</div>
					</div>

					<p
						v-if="columns.includes(COLUMN_AVAILABILITY)"
						:data-column="COLUMN_AVAILABILITY"
						class="time-off-list-popover__availability"
					>
						{{ getFormattedAvailability(availability) }}
					</p>
					<time
						v-if="columns.includes(COLUMN_PERIOD)"
						:data-column="COLUMN_PERIOD"
						class="time-off-list-popover__period"
					>
						{{ getFormattedPeriod(timeOffs) }}
					</time>
					<div
						class="time-off-list-popover__buttons-wrapper"
						data-column="buttons"
					>
						<AppDropdown
							v-if="
								timeOffs?.length === 1 &&
								!timeOffs[0].externalSystem &&
								canAddAndModifyTimeOff &&
								(canChangeTimeOffForOthers ||
									currentUser?.id === user.id)
							"
							:popover-props="{
								distance: 0,
								placement: 'bottom',
								autoHide: false,
							}"
							:trigger-button-props="{
								'v-tooltip':
									!canChangeTimeOffForOthers &&
									currentUser?.id === user.id &&
									timeOffs[0].hasMultipleUsers
										? $t('time-off__no-permission')
										: $t('time-off__modify'),
								'aria-label':
									!canChangeTimeOffForOthers &&
									currentUser?.id === user.id &&
									timeOffs[0].hasMultipleUsers
										? $t('time-off__no-permission')
										: $t('time-off__modify'),
								disabled:
									!canChangeTimeOffForOthers &&
									currentUser?.id === user.id &&
									timeOffs[0].hasMultipleUsers,
							}"
						>
							<template #trigger>
								<SuiIcon
									name="icon-pencil-16"
									class="time-off-list-popover__modify-button"
									:class="{
										'time-off-list-popover__modify-button--disabled':
											!canChangeTimeOffForOthers &&
											currentUser?.id === user.id &&
											timeOffs[0].hasMultipleUsers,
									}"
								/>
							</template>

							<template #default="{ closeDropdown, isOpen }">
								<TimeOffModifyForm
									:id="timeOffs[0].id"
									:is-open="isOpen"
									@close="closeDropdown"
									@save="onSave"
								/>
							</template>
						</AppDropdown>

						<AppConfirmationPopover
							v-if="
								timeOffs?.length === 1 &&
								!timeOffs[0].externalSystem &&
								canAddAndModifyTimeOff &&
								(canChangeTimeOffForOthers ||
									currentUser?.id === user.id)
							"
							v-slot="{ openConfirmation }"
							message="time-off__reset-availability-confirmation"
							:message-value="{
								userName: user.name,
								period: getFormattedPeriod(timeOffs),
							}"
							:primary-button-label="
								$t(
									'time-off__reset-availability-confirmation-label'
								)
							"
							:secondary-button-label="
								$t('time-off__reset-availability-cancel-label')
							"
							@confirm="onDelete(timeOffs[0].id, user)"
						>
							<SuiTextButton
								v-tooltip="$t('time-off__delete')"
								class="time-off-list-popover__delete-button"
								:aria-label="$t('time-off__delete')"
								@click="openConfirmation"
							>
								<SuiIcon
									class="time-off-list-popover__delete-button"
									name="icon-bin-16"
								/>
							</SuiTextButton>
						</AppConfirmationPopover>
					</div>
				</li>
			</ul>
		</article>
	</AppDropdown>
</template>

<script setup lang="ts">
import { SuiAvatar, SuiIcon, SuiTextButton } from '@developers/scoro-ui';
import { computed, toRefs } from 'vue';
import { useFormatter, usePermissions, useTranslation } from '@/composables';
import {
	type User,
	useTimeOffDeleteMutation,
	type TimeOff,
	useTimeOffListPopoverQuery,
	type TimeOffListPopoverQuery,
} from '@/generated/graphql';
import {
	type TimeOffPopoverColumn,
	COLUMN_USER,
	COLUMN_AVAILABILITY,
	COLUMN_PERIOD,
	type UserTimeOffs,
} from './types';
import { useMessage } from '@/features/Message';
import { PermissionAction, Permission } from '@/constants/permissions';
import AppConfirmationPopover from '@/features/App/AppConfirmationPopover.vue';
import AppDropdown from '@/features/App/AppDropdown/AppDropdown.vue';
import TimeOffListPopoverDropdown from './TimeOffListPopoverDropdown.vue';
import { getEarliestDate, getLatestDate } from './helpers';
import TimeOffListPopoverSkeleton from './TimeOffListPopoverSkeleton.vue';
import TimeOffEntryLabel from './TimeOffEntryLabel.vue';
import TimeOffModifyForm from './TimeOffModifyForm.vue';
import { useCurrentUser } from '@/features/Auth';
import { type SAPIDate } from '@/types/sapi/scalars';

export type TimeOffListPopoverProps = {
	userIds: Array<User['id']>;
	startDate: SAPIDate;
	endDate?: SAPIDate;
	columns?: Array<TimeOffPopoverColumn>;
};

const emit = defineEmits<{
	(e: 'close'): void;
	(e: 'save'): void;
}>();

const props = withDefaults(defineProps<TimeOffListPopoverProps>(), {
	endDate: '',
	columns: () => [COLUMN_USER, COLUMN_AVAILABILITY, COLUMN_PERIOD],
});

const { formatDateNoCurrentYear: formatDate, formatLongDuration } =
	useFormatter();
const { t } = useTranslation();
const { addSuccessMessage } = useMessage();
const { can } = usePermissions();
const { currentUser } = useCurrentUser();

const { userIds, startDate, endDate } = toRefs(props);

const { loading: loadingTimeOffList, result: resultTimeOffList } =
	useTimeOffListPopoverQuery(
		{
			input: {
				filters: {
					userIds: userIds.value,
					startDate: startDate.value,
					endDate: endDate?.value ?? startDate.value,
				},
			},
		},
		{
			fetchPolicy: 'no-cache',
			notifyOnNetworkStatusChange: true,
		}
	);

const { mutate: deleteTimeOff } = useTimeOffDeleteMutation({});

const usersTimeOffs = computed(() => {
	const data = resultTimeOffList.value?.UserTimeOffList;

	const uniqueUserIds = Array.from(new Set(data?.map(({ user }) => user.id)));
	const usersWithTimeOffs: Array<UserTimeOffs> = [];

	uniqueUserIds.forEach((userId) => {
		const uniqueUserTimeOffs = data?.reduce<
			TimeOffListPopoverQuery['UserTimeOffList'][0][]
		>((acc, current) => {
			if (current.user.id === userId) {
				const isDuplicate = acc.some(
					(item) => item.timeOff.id === current.timeOff.id
				);

				if (!isDuplicate) {
					acc.push(current);
				}
			}

			return acc;
		}, []);

		if (uniqueUserTimeOffs) {
			const { user, availability } = uniqueUserTimeOffs[0];

			const timeOffs = uniqueUserTimeOffs.map(
				({ timeOff, value, hasMultipleUsers }) => ({
					...timeOff,
					value,
					hasMultipleUsers,
				})
			);

			usersWithTimeOffs.push({
				user,
				timeOffs,
				availability,
			});
		}
	});

	return usersWithTimeOffs;
});

const canAddAndModifyTimeOff = computed(() =>
	can(PermissionAction.HAS_PERMISSION, Permission.CAN_ADD_AND_MODIFY_TIMEOFF)
);

const canChangeTimeOffForOthers = computed(() =>
	can(
		PermissionAction.HAS_PERMISSION,
		Permission.CAN_CHANGE_TIMEOFF_FOR_OTHER_USERS
	)
);

const getFormattedPeriod = (timeOffs?: TimeOff[]) => {
	if (timeOffs && timeOffs.length > 0) {
		const startDates = timeOffs.map(({ startDate }) => startDate);
		const earliestDate = getEarliestDate(startDates);

		const endDates = timeOffs.map(({ endDate }) => endDate);
		const latestDate = getLatestDate(endDates);

		const startDate = formatDate(earliestDate);
		const endDate = formatDate(latestDate);

		return `${startDate} - ${endDate}`;
	}

	return '-';
};

const getFormattedAvailability = (value: number) => {
	if (value > 0) {
		return formatLongDuration({
			duration: value,
			removeMinutesOnFullHours: true,
		});
	}

	return t('time-off__not-available');
};

const onSave = () => {
	emit('save');
};

const onDelete = async (id: TimeOff['id'], user: UserTimeOffs['user']) => {
	const response = await deleteTimeOff({
		input: {
			id,
			userIds: [user.id],
		},
	});

	if (!response?.errors) {
		addSuccessMessage({
			message: t('time-off__save-success-one-user', {
				userName: user.name,
			}),
		});

		onSave();
	}
};
</script>

<style lang="scss">
.time-off-list-popover {
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	box-shadow: $scoro-box-shadow-modal;
	height: 100%;
	width: 100%;
	max-width: 70rem;
	border-radius: 1rem;
	gap: 0.125rem;
	background-color: $neutral-00;

	&__header {
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		align-self: stretch;
		box-shadow: $scoro-box-shadow-short-bottom;
		gap: 0.125rem;
		padding: 2rem 2.5rem 0;
	}

	&__column-header {
		color: $neutral-70;
		font-size: 1.375rem;
		text-align: right;
		font-weight: $scoro-fw-normal;

		&:first-of-type {
			justify-self: start;
		}
	}

	&__row {
		display: grid;
		grid-template-columns:
			[user-column] 28rem [availability-column] 10rem [period-column] minmax(
				16rem,
				1fr
			)
			[buttons-column] 6rem;
		gap: 1rem;
		justify-items: end;
		width: 100%;

		[data-column='user'] {
			grid-column-start: user-column;
		}

		[data-column='availability'] {
			grid-column-start: availability-column;
		}

		[data-column='period'] {
			grid-column-start: period-column;
		}

		[data-column='buttons'] {
			grid-column-start: buttons-column;
		}

		&--header {
			padding: 0.5rem 0;
			align-items: center;
		}

		&--list {
			padding: 1.5rem 0.5rem;
			align-items: start;

			&:not(:last-of-type) {
				border-bottom: 1px solid $neutral-20;
			}
		}
	}

	&__list.time-off-list-popover__list {
		display: flex;
		max-height: 40rem;
		padding: 0 2rem;
		overflow-y: auto;
		flex-direction: column;
		align-items: flex-start;
		align-self: stretch;
		gap: 0.125rem;
	}

	&__user-wrapper {
		justify-self: start;
	}

	&__user-details {
		display: flex;
		align-items: center;
		gap: 1rem;
	}

	&__time-off-entries-wrapper {
		margin: 0.25rem 0 0 3.5rem;
	}

	&__time-off-entries {
		font-size: 1.5rem;
		color: $neutral-80;
		display: flex;
		align-items: center;
		justify-content: flex-start;
		gap: 0.5rem;

		&--active {
			&:hover {
				color: $neutral-90;
			}
		}
	}

	&__labels {
		display: inline-flex;
		align-items: center;
		flex-wrap: wrap;
	}

	&__icon {
		&:hover {
			--sui-icon-fill: #{$neutral-70};
		}
	}

	&__highlighted {
		font-size: 1.625rem;
		font-weight: $scoro-fw-semi-bold;
	}

	&__period,
	&__availability {
		text-align: right;
	}

	&__buttons-wrapper {
		display: flex;
		gap: 1rem;
	}

	&__modify-button {
		&:hover:not(&--disabled) {
			--sui-icon-fill: #{$neutral-70};
		}

		&--disabled {
			--sui-icon-fill: #{$neutral-50};
		}
	}

	&__delete-button {
		--sui-color-link: #{$neutral-60};
		--sui-color-link-hover: #{$neutral-70};
	}
}
</style>
